import React, { useState } from 'react';
import { readConfObject, } from '@jbrowse/core/configuration';
import { bpSpanPx, getStrokeProps } from '@jbrowse/core/util';
import { observer } from 'mobx-react';
// locals
import ArcTooltip from '../ArcTooltip';
function Arc({ selectedFeatureId, region, bpPerPx, config, onFeatureClick, feature, }) {
    const [isMouseOvered, setIsMouseOvered] = useState(false);
    const [left, right] = bpSpanPx(feature.get('start'), feature.get('end'), region, bpPerPx);
    const featureId = feature.id();
    let stroke = readConfObject(config, 'color', { feature });
    let textStroke = 'black';
    if (selectedFeatureId && String(selectedFeatureId) === String(feature.id())) {
        stroke = textStroke = 'red';
    }
    const label = readConfObject(config, 'label', { feature });
    const caption = readConfObject(config, 'caption', { feature });
    const strokeWidth = readConfObject(config, 'thickness', { feature }) || 1;
    const height = readConfObject(config, 'height', { feature }) || 100;
    const ref = React.createRef();
    // formula: https://en.wikipedia.org/wiki/B%C3%A9zier_curve#Cubic_B%C3%A9zier_curves
    const t = 0.5;
    const t1 = 1 - t;
    const textYCoord = 3 * (t1 * t1) * (t * height) + 3 * t1 * (t * t) * height;
    return (React.createElement("g", null,
        React.createElement("path", { ref: ref, ...getStrokeProps(stroke), d: `M ${left} 0 C ${left} ${height}, ${right} ${height}, ${right} 0`, strokeWidth: strokeWidth, fill: "transparent", onClick: e => onFeatureClick === null || onFeatureClick === void 0 ? void 0 : onFeatureClick(e, featureId), onMouseOver: () => setIsMouseOvered(true), onMouseLeave: () => setIsMouseOvered(false), pointerEvents: "stroke" }),
        isMouseOvered ? React.createElement(ArcTooltip, { contents: caption }) : null,
        React.createElement("text", { x: left + (right - left) / 2, y: textYCoord + 3, stroke: "white", strokeWidth: "0.6em" }, label),
        React.createElement("text", { x: left + (right - left) / 2, y: textYCoord + 3, stroke: textStroke }, label)));
}
function polarToCartesian(centerX, centerY, radius, angleInDegrees) {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180;
    return {
        x: centerX + radius * Math.cos(angleInRadians),
        y: centerY + radius * Math.sin(angleInRadians),
    };
}
function describeArc(x, y, radius, startAngle, endAngle) {
    const start = polarToCartesian(x, y, radius, endAngle);
    const end = polarToCartesian(x, y, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
    return [
        'M',
        start.x,
        start.y,
        'A',
        radius,
        radius,
        0,
        largeArcFlag,
        0,
        end.x,
        end.y,
    ].join(' ');
}
function SemiCircles({ selectedFeatureId, region, bpPerPx, config, onFeatureClick, feature, }) {
    const [isMouseOvered, setIsMouseOvered] = useState(false);
    const [left, right] = bpSpanPx(feature.get('start'), feature.get('end'), region, bpPerPx);
    const featureId = feature.id();
    let stroke = readConfObject(config, 'color', { feature });
    let textStroke = 'black';
    if (selectedFeatureId && String(selectedFeatureId) === String(feature.id())) {
        stroke = textStroke = 'red';
    }
    const label = readConfObject(config, 'label', { feature });
    const caption = readConfObject(config, 'caption', { feature });
    const strokeWidth = readConfObject(config, 'thickness', { feature }) || 1;
    const ref = React.createRef();
    const textYCoord = (right - left) / 2;
    return (React.createElement("g", null,
        React.createElement("path", { d: describeArc(left + (right - left) / 2, 0, (right - left) / 2, 90, 270), ...getStrokeProps(stroke), strokeWidth: strokeWidth, fill: "transparent", onClick: e => onFeatureClick === null || onFeatureClick === void 0 ? void 0 : onFeatureClick(e, featureId), onMouseOver: () => setIsMouseOvered(true), onMouseLeave: () => setIsMouseOvered(false), ref: ref, pointerEvents: "stroke" }),
        isMouseOvered ? React.createElement(ArcTooltip, { contents: caption }) : null,
        React.createElement("text", { x: left + (right - left) / 2, y: textYCoord + 3, stroke: "white", strokeWidth: "0.6em" }, label),
        React.createElement("text", { x: left + (right - left) / 2, y: textYCoord + 3, stroke: textStroke }, label)));
}
const ArcRendering = observer(function ({ features, config, regions, bpPerPx, height, exportSVG, onFeatureClick, displayModel: { selectedFeatureId }, }) {
    const [region] = regions;
    const width = (region.end - region.start) / bpPerPx;
    const semicircles = readConfObject(config, 'displayMode') === 'semicircles';
    return (React.createElement(Wrapper, { exportSVG: exportSVG, width: width, height: height }, [...features.values()].map(f => semicircles ? (React.createElement(SemiCircles, { key: f.id(), config: config, region: region, bpPerPx: bpPerPx, selectedFeatureId: selectedFeatureId, onFeatureClick: onFeatureClick, feature: f })) : (React.createElement(Arc, { key: f.id(), config: config, region: region, bpPerPx: bpPerPx, selectedFeatureId: selectedFeatureId, onFeatureClick: onFeatureClick, feature: f })))));
});
function Wrapper({ exportSVG, width, height, children, }) {
    return exportSVG ? (children) : (React.createElement("svg", { width: width, height: height }, children));
}
export default ArcRendering;
